import React from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Briefcase, Clock, User, AlertTriangle, Maximize2, CheckSquare, MessageSquare, XCircle,
} from 'lucide-react';
import api from '../../utils/axios';
import { toast } from 'sonner';

export const AlertsDialogs = ({
  getSeverityColor,
  // Detail dialog
  showDetailDialog, setShowDetailDialog, selectedAlert,
  alertComments, loadAlerts,
  // Full detail drawer
  showFullDetailDrawer, setShowFullDetailDrawer, fullDetailData,
  // Create case dialog
  showCreateCaseDialog, setShowCreateCaseDialog, alertForCase, setAlertForCase,
  handleCreateCaseFromAlert, creatingCase,
  // Assign dialog
  showAssignDialog, setShowAssignDialog, pendingMove, setPendingMove,
  selectedAssignee, setSelectedAssignee, handleConfirmAssignment,
  users, currentUser,
  // Multi case dialog
  showMultiCaseDialog, setShowMultiCaseDialog,
  selectedAlerts, alerts, toggleAlertSelection,
  multiCaseData, setMultiCaseData, handleCreateMultiCase,
  // Open dialogs
  openCreateCaseDialog,
}) => (
  <>
    {/* Modal de Vista Rápida de Alerta */}
    <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" style={{ backgroundColor: '#ffffff' }}>
        {selectedAlert && (
          <>
            <DialogHeader className="flex-shrink-0 pb-4 border-b">
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="text-amber-500" />Vista Rápida de Alerta
              </DialogTitle>
              <div className="flex flex-wrap gap-2 items-center mt-2">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getSeverityColor(selectedAlert.severidad)}`}>{selectedAlert.severidad}</span>
                <Badge variant="outline" className="text-sm">{selectedAlert.estado.replace('_', ' ')}</Badge>
                {selectedAlert.alerta_padre_id && <Badge className="bg-purple-100 text-purple-700">Relacionada</Badge>}
              </div>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto py-4 space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">{selectedAlert.titulo}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{selectedAlert.descripcion}</p>
              </div>
              <Card className="p-4 bg-slate-50">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><Label className="text-xs text-slate-500">Fecha de Creación</Label>
                    <p className="text-slate-900 flex items-center gap-1 mt-1"><Clock size={14} />{new Date(selectedAlert.created_at).toLocaleString('es-AR')}</p></div>
                  {selectedAlert.cuenta_id && (
                    <div><Label className="text-xs text-slate-500">Cuenta Asociada</Label>
                      {selectedAlert.razon_social && <p className="font-medium text-slate-900 mt-1">{selectedAlert.razon_social}</p>}
                      <p className="font-mono text-slate-600 mt-0.5 text-sm">{selectedAlert.cuenta_id}</p></div>
                  )}
                  {selectedAlert.tomada_por && (
                    <div><Label className="text-xs text-slate-500">Tomada Por</Label>
                      <p className="text-slate-900 flex items-center gap-1 mt-1"><User size={14} />{selectedAlert.tomada_por}</p></div>
                  )}
                  {selectedAlert.caso_id && (
                    <div><Label className="text-xs text-slate-500">Caso Vinculado</Label>
                      <p className="font-mono text-blue-600 mt-1">{selectedAlert.caso_id}</p></div>
                  )}
                  {selectedAlert.cuit_cliente && (
                    <div><Label className="text-xs text-slate-500">CUIT Cliente</Label>
                      <p className="font-mono text-slate-900 mt-1">{selectedAlert.cuit_cliente}</p></div>
                  )}
                  {selectedAlert.tipo && (
                    <div><Label className="text-xs text-slate-500">Tipo de Alerta</Label>
                      <p className="text-slate-900 mt-1">{selectedAlert.tipo}</p></div>
                  )}
                </div>
              </Card>
              {selectedAlert.resolucion && (
                <Card className="p-4 bg-green-50 border-green-200">
                  <Label className="text-xs text-green-700 mb-2 block">Resolución</Label>
                  <p className="text-sm text-green-900">{selectedAlert.resolucion}</p>
                </Card>
              )}
              {selectedAlert.movimientos_ids?.length > 0 && (
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <div className="flex items-center justify-between">
                    <div><Label className="text-xs text-blue-700 mb-1 block">Movimientos Asociados</Label>
                      <p className="text-lg font-bold text-blue-900">{selectedAlert.movimientos_ids.length} transacciones</p></div>
                    <Badge className="bg-blue-100 text-blue-700">Ver en detalle completo</Badge>
                  </div>
                </Card>
              )}
              {alertComments.length > 0 && (
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1 mb-3"><MessageSquare size={14} />Comentarios ({alertComments.length})</Label>
                  <div className="space-y-2 max-h-36 overflow-y-auto">
                    {alertComments.slice(0, 3).map((comment, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg text-sm">
                        <p className="text-slate-900">{comment.texto}</p>
                        <div className="flex justify-between mt-2 text-xs text-slate-500">
                          <span className="font-medium">{comment.usuario}</span>
                          <span>{new Date(comment.fecha).toLocaleString('es-AR')}</span>
                        </div>
                      </div>
                    ))}
                    {alertComments.length > 3 && <p className="text-xs text-slate-500 text-center py-1">+{alertComments.length - 3} comentarios más</p>}
                  </div>
                </div>
              )}
            </div>
            <div className="flex-shrink-0 flex justify-between items-center pt-4 border-t mt-2">
              <div className="flex gap-2">
                {selectedAlert.estado === 'NUEVA' && (
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={async () => {
                      try { await api.post(`/alerts/${selectedAlert.id}/tomar`); toast.success('Alerta tomada correctamente'); setShowDetailDialog(false); loadAlerts(); }
                      catch { toast.error('Error al tomar la alerta'); }
                    }}><CheckSquare size={14} className="mr-1" />Tomar Alerta</Button>
                )}
              </div>
              <Button onClick={() => setShowFullDetailDrawer(true)} className="gap-2" data-testid="view-full-detail-btn">
                <Maximize2 size={16} />Ver Detalle Completo
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>

    {/* Dialog para crear caso desde alerta */}
    <Dialog open={showCreateCaseDialog} onOpenChange={(open) => { if (!open) { setShowCreateCaseDialog(false); setAlertForCase(null); } }}>
      <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Briefcase className="text-green-600" size={20} />Crear Caso desde Alerta</DialogTitle>
        </DialogHeader>
        {alertForCase && (
          <div className="py-4">
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <p className="text-sm text-slate-600 mb-2"><strong>Alerta:</strong> {alertForCase.titulo}</p>
              <p className="text-xs text-slate-500 mb-2">{alertForCase.descripcion?.substring(0, 150)}...</p>
              <div className="flex items-center gap-2 text-xs">
                <span className={`px-2 py-0.5 rounded ${getSeverityColor(alertForCase.severidad)}`}>{alertForCase.severidad}</span>
                {alertForCase.cuenta_id && <span className="text-slate-400">Cuenta: {alertForCase.cuenta_id}</span>}
              </div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">Se creará un caso de tipo <strong>{alertForCase.severidad === 'CRITICA' || alertForCase.severidad === 'ALTA' ? 'FRAUDE' : 'ALERTA'}</strong> con prioridad <strong>{alertForCase.severidad}</strong> vinculado a esta alerta.</p>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => { setShowCreateCaseDialog(false); setAlertForCase(null); }}>Cancelar</Button>
          <Button onClick={handleCreateCaseFromAlert} disabled={creatingCase} style={{ backgroundColor: '#059669' }}>{creatingCase ? 'Creando...' : 'Crear Caso'}</Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Dialog para asignar al mover tarjeta */}
    <Dialog open={showAssignDialog} onOpenChange={(open) => { if (!open) { setShowAssignDialog(false); setPendingMove(null); } }}>
      <DialogContent className="max-w-md" style={{ backgroundColor: '#ffffff' }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><User className="text-blue-600" size={20} />Asignar Alerta</DialogTitle>
        </DialogHeader>
        {pendingMove && (
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">Alerta: <strong>"{pendingMove.alert?.titulo}"</strong></p>
            <Button className="w-full" style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
              onClick={() => { setSelectedAssignee(currentUser?.id); handleConfirmAssignment(); }}>
              <User size={16} className="mr-2" />Asignármela a mí
            </Button>
            <div className="relative">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200" /></div>
              <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-slate-500">O asignar a otro usuario</span></div>
            </div>
            <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
              <SelectTrigger className="w-full" style={{ backgroundColor: '#ffffff' }}><SelectValue placeholder="Seleccionar usuario..." /></SelectTrigger>
              <SelectContent>{users.map(user => <SelectItem key={user.id} value={user.id}>{user.nombre} {user.id === currentUser?.id ? '(Yo)' : ''} - {user.email}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => { setShowAssignDialog(false); setPendingMove(null); }} style={{ backgroundColor: '#ffffff', color: '#475569' }}>Cancelar</Button>
          <Button onClick={handleConfirmAssignment} disabled={!selectedAssignee} style={{ backgroundColor: '#2563eb', color: '#ffffff' }}>Asignar y Mover</Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Dialog para crear caso con múltiples alertas */}
    <Dialog open={showMultiCaseDialog} onOpenChange={setShowMultiCaseDialog}>
      <DialogContent className="max-w-lg" style={{ backgroundColor: '#ffffff' }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Briefcase className="text-blue-600" size={24} />Crear Caso con Múltiples Alertas</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-blue-900">Alertas seleccionadas</span>
              <Badge className="bg-blue-600 text-white">{selectedAlerts.length}</Badge>
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {alerts.filter(a => selectedAlerts.includes(a.id)).map(alert => (
                <div key={alert.id} className="flex items-center gap-2 text-sm text-blue-800 bg-white/50 p-2 rounded">
                  <span className={`px-1.5 py-0.5 rounded text-xs ${getSeverityColor(alert.severidad)}`}>{alert.severidad}</span>
                  <span className="truncate flex-1">{alert.titulo}</span>
                  <button onClick={() => toggleAlertSelection(alert.id)} className="text-blue-400 hover:text-red-500">×</button>
                </div>
              ))}
            </div>
          </div>
          <div><Label htmlFor="case-title">Título del Caso *</Label>
            <Input id="case-title" value={multiCaseData.titulo} onChange={(e) => setMultiCaseData(d => ({ ...d, titulo: e.target.value }))}
              placeholder="Ej: Investigación de red sospechosa" style={{ backgroundColor: '#fff' }} data-testid="multi-case-title" /></div>
          <div><Label htmlFor="case-desc">Descripción</Label>
            <Textarea id="case-desc" value={multiCaseData.descripcion} onChange={(e) => setMultiCaseData(d => ({ ...d, descripcion: e.target.value }))}
              placeholder="Describa el caso de investigación..." rows={3} style={{ backgroundColor: '#fff' }} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Tipo de Caso</Label>
              <Select value={multiCaseData.tipo} onValueChange={(v) => setMultiCaseData(d => ({ ...d, tipo: v }))}>
                <SelectTrigger style={{ backgroundColor: '#fff' }}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALERTA">Alerta</SelectItem><SelectItem value="FRAUDE">Fraude</SelectItem>
                  <SelectItem value="COMPLIANCE">Compliance</SelectItem><SelectItem value="SOPORTE">Soporte</SelectItem><SelectItem value="OTRO">Otro</SelectItem>
                </SelectContent></Select></div>
            <div><Label>Prioridad</Label>
              <Select value={multiCaseData.prioridad} onValueChange={(v) => setMultiCaseData(d => ({ ...d, prioridad: v }))}>
                <SelectTrigger style={{ backgroundColor: '#fff' }}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CRITICA">Crítica</SelectItem><SelectItem value="ALTA">Alta</SelectItem>
                  <SelectItem value="MEDIA">Media</SelectItem><SelectItem value="BAJA">Baja</SelectItem>
                </SelectContent></Select></div>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setShowMultiCaseDialog(false)}>Cancelar</Button>
          <Button onClick={handleCreateMultiCase} disabled={creatingCase || !multiCaseData.titulo.trim()}
            style={{ backgroundColor: '#2563eb' }} data-testid="confirm-multi-case-btn">
            {creatingCase ? 'Creando...' : `Crear Caso con ${selectedAlerts.length} Alertas`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Full Detail Drawer (Panel Lateral) */}
    {showFullDetailDrawer && selectedAlert && (
      <div className="fixed inset-0 z-50 flex">
        <div className="absolute inset-0 bg-black/40" onClick={() => setShowFullDetailDrawer(false)} />
        <div className="ml-auto relative bg-white w-full max-w-2xl h-full overflow-y-auto shadow-xl animate-in slide-in-from-right">
          <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
            <h2 className="text-lg font-semibold text-slate-900">Detalle de Alerta</h2>
            <Button variant="ghost" size="sm" onClick={() => setShowFullDetailDrawer(false)}><XCircle size={20} /></Button>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className={getSeverityColor(selectedAlert.severidad)}>{selectedAlert.severidad}</Badge>
                <Badge variant="outline">{selectedAlert.estado}</Badge>
              </div>
              <h3 className="text-xl font-bold text-slate-900">{selectedAlert.titulo}</h3>
              <p className="text-sm text-slate-500 mt-1">{new Date(selectedAlert.created_at).toLocaleString('es-AR')}</p>
            </div>
            <Card className="p-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Descripción</h4>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedAlert.descripcion || fullDetailData?.descripcion || 'Sin descripción'}</p>
            </Card>
            {(selectedAlert.cuenta_id || selectedAlert.razon_social) && (
              <Card className="p-4">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Cuenta Asociada</h4>
                {selectedAlert.razon_social && <p className="font-medium text-slate-900">{selectedAlert.razon_social}</p>}
                {selectedAlert.cuenta_id && <p className="text-sm text-slate-500 font-mono">{selectedAlert.cuenta_id}</p>}
              </Card>
            )}
            {fullDetailData && (
              <>
                {fullDetailData.movimientos_ids?.length > 0 && (
                  <Card className="p-4"><h4 className="text-sm font-semibold text-slate-700 mb-2">Movimientos Asociados</h4>
                    <p className="text-sm text-slate-600">{fullDetailData.movimientos_ids.length} movimiento(s) vinculado(s)</p></Card>
                )}
                {fullDetailData.regla_disparadora && (
                  <Card className="p-4"><h4 className="text-sm font-semibold text-slate-700 mb-2">Regla Disparadora</h4>
                    <p className="text-sm text-slate-600">{fullDetailData.regla_disparadora}</p></Card>
                )}
                {fullDetailData.parametros_alerta && Object.keys(fullDetailData.parametros_alerta).length > 0 && (
                  <Card className="p-4"><h4 className="text-sm font-semibold text-slate-700 mb-2">Parámetros</h4>
                    <div className="space-y-1">
                      {Object.entries(fullDetailData.parametros_alerta).map(([k, v]) => (
                        <div key={k} className="flex justify-between text-sm">
                          <span className="text-slate-500">{k}</span>
                          <span className="text-slate-700 font-medium">{typeof v === 'number' && (k.includes('monto') || k.includes('volumen')) ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(v) : String(v)}</span>
                        </div>
                      ))}
                    </div></Card>
                )}
              </>
            )}
            <div className="flex gap-3 pt-4 border-t">
              {selectedAlert.estado === 'NUEVA' && (
                <Button className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={async () => {
                    try { await api.post(`/alerts/${selectedAlert.id}/tomar`); toast.success('Alerta tomada correctamente');
                      setShowFullDetailDrawer(false); setShowDetailDialog(false); loadAlerts(); }
                    catch { toast.error('Error al tomar la alerta'); }
                  }}>Tomar Alerta</Button>
              )}
              <Button variant="outline" onClick={() => { setShowFullDetailDrawer(false); setShowDetailDialog(false); openCreateCaseDialog(selectedAlert); }}>
                <Briefcase size={16} className="mr-2" />Crear Caso
              </Button>
            </div>
          </div>
        </div>
      </div>
    )}
  </>
);
